import React from 'react';
import {
  SOCIAL_POST_STATUS_DRAFT,
  SOCIAL_POST_STATUS_FAILED,
  SOCIAL_POST_STATUS_PUBLISHED,
  SocialMediaContent,
  SocialMediaPost,
} from '../../types';
import {
  FacebookPageDto,
  InstagramAccountDto,
  SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE,
  SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT,
} from '../../../social-posting/socialPostingApi';
import facebookLogo from '../../../assets/images/facebook.png';
import instagramLogo from '../../../assets/images/instagram.png';
import { NewTabLink } from '../../../commons/components/NewTabLink';
import { GrowegyCheckbox } from '../GrowegyCheckbox';
import { OpenExternalIcon } from '../../../assets/icons';
import { getAggregatePostStatus } from './utils';
import clsx from 'clsx';

export const SocialMediaAccountPost = (props: {
  post: SocialMediaPost | null;
  account: FacebookPageDto | InstagramAccountDto | null;
  content: SocialMediaContent;
  publishOn: Date;
  readonly: boolean;
  onSelectionChange: (isSelected: boolean) => void;
}) => {
  const { post, account, publishOn, content, readonly, onSelectionChange } = props;
  if (!post && !account) {
    return null;
  }

  const isFacebook =
    post?.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE ||
    account?.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE;
  const isInstagram =
    post?.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT ||
    account?.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT;

  const isChecked = post !== null;
  const disabled =
    (post &&
      post.status !== SOCIAL_POST_STATUS_DRAFT &&
      post.status !== SOCIAL_POST_STATUS_FAILED) ||
    !account;

  const getSocialIcon = () => {
    if (isFacebook) {
      return <img src={facebookLogo} alt="Facebook" className="social-media-account__icon" />;
    }
    if (isInstagram) {
      return <img src={instagramLogo} alt="Instagram" className="social-media-account__icon" />;
    }
    return null;
  };

  const getNameText = () => {
    if (!account) {
      return 'Unknown';
    }

    if (isFacebook && account.type === SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE) {
      return account.name;
    }

    if (isInstagram && account.type === SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT) {
      return `@${account.username}`;
    }

    return 'Unknown';
  };

  const getViewButtonUrl = () => {
    if (post?.status === SOCIAL_POST_STATUS_PUBLISHED && post.publishing?.postId) {
      switch (post.type) {
        case SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE:
          return `https://facebook.com/${post.publishing.postId}`;
        case SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT:
          if (post.publishing.instagram?.permalink) {
            return post.publishing.instagram?.permalink;
          } else if (post.publishing.instagram?.shortCode) {
            return `https://instagram.com/p/${post.publishing.instagram.shortCode}`;
          }
          break;
        default:
          return null;
      }
    }

    switch (account?.type) {
      case SOCIAL_MEDIA_ASSET_FACEBOOK_PAGE:
        return `https://facebook.com/${account.id}`;
      case SOCIAL_MEDIA_ASSET_INSTAGRAM_ACCOUNT:
        return `https://instagram.com/${account.username}`;
      default:
        return null;
    }
  };

  const viewUrl = getViewButtonUrl();

  const onCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSelectionChange(event.target.checked);
  };

  const aggregateStatus = getAggregatePostStatus(content, publishOn, post ? [post] : []);

  return (
    <div className="social-media-account__container">
      <div className="social-media-account__line-container">
        <GrowegyCheckbox
          dataTestPrefix="social-media-account"
          checked={isChecked}
          disabled={disabled || readonly}
          narrow
          labelClassName="social-media-account__label"
          onChange={onCheckboxChange}
        >
          <>
            {getSocialIcon()}
            <span className="text-truncate">{getNameText()}</span>
          </>
        </GrowegyCheckbox>
        {viewUrl && (
          <NewTabLink href={viewUrl}>
            <OpenExternalIcon className="social-media-account__open-link" />
          </NewTabLink>
        )}
      </div>
      <div>
        {post && (
          <div
            className={clsx('social-media-account__status', {
              'social-media-account__status--error': aggregateStatus.status === 'ERROR',
            })}
          >
            {aggregateStatus.status === 'SUCCESS'
              ? '✅ '
              : aggregateStatus.status === 'PENDING'
              ? '🕐 '
              : aggregateStatus.status === 'ERROR'
              ? '🔴 '
              : '⚠️ '}
            {aggregateStatus.description}
          </div>
        )}
      </div>
    </div>
  );
};
